import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Music2, Loader2, Download, Radio, Play, Pause, SkipForward, Shuffle, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import MisteckMark from "../components/brand/MisteckMark";

const API = process.env.REACT_APP_BACKEND_URL;
const ACTIVITIES = ["By myself gym pump", "Late-night drive", "Study focus session", "Morning run", "Chill sunday morning", "Party pregame", "Romantic dinner", "Deep work flow", "Rainy day indoors", "Road trip anthem"];
const lsKey = (id) => `gm_${id}`;

export default function MusicBuyASong() {
  const nav = useNavigate();
  const [genre, setGenre] = useState("");
  const [mood, setMood] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [lyricsRem, setLyricsRem] = useState(3);
  const [lyricsBusy, setLyricsBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("idle"); // idle(compose) | lyrics | starting | processing | ready | failed
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloads, setDownloads] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [shuffle, setShuffle] = useState(false);
  const [prog, setProg] = useState(0);
  const audioRef = useRef(null);
  const pollRef = useRef(null);

  // On return from Stripe: resume polling the order captured before redirect.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const order = q.get("order");
    if (q.get("cancel")) setPhase("idle");
    if (order) {
      const token = localStorage.getItem(lsKey(order));
      if (token) startPolling(order, token);
    }
    fetch(`${API}/api/guest-music/radio`).then((r) => r.json()).then((d) => setTracks(d.tracks || [])).catch(() => {});
    // Page-visit beacon (founder funnel tile) — persistent per-device visitor id.
    try {
      let vid = localStorage.getItem("gm_visitor_id");
      if (!vid) { vid = (crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2)); localStorage.setItem("gm_visitor_id", vid); }
      fetch(`${API}/api/guest-music/track-view`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: vid }),
      }).catch(() => {});
    } catch { /* ignore */ }
    return () => clearInterval(pollRef.current);
  }, []);

  const startPolling = (order, token) => {
    setPhase("processing");
    clearInterval(pollRef.current);
    const tick = () => {
      fetch(`${API}/api/guest-music/order/${order}?token=${token}`).then((r) => r.json()).then((d) => {
        if (d.status === "ready") {
          setDownloadUrl(`${API}${d.download_url}`); setDownloads(d.downloads || null);
          setPhase("ready"); clearInterval(pollRef.current);
        } else if (d.status === "failed") {
          setPhase("failed"); clearInterval(pollRef.current);
        }
      }).catch(() => {});
    };
    tick();
    pollRef.current = setInterval(tick, 5000);
  };

  const previewLyrics = async () => {
    if (genre.trim().length < 2 || mood.trim().length < 3 || lyricsBusy) return;
    setLyricsBusy(true);
    try {
      const r = await fetch(`${API}/api/guest-music/lyrics`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ genre, mood }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Couldn't write lyrics");
      setLyrics(d.lyrics || "");
      setLyricsRem(typeof d.remaining === "number" ? d.remaining : 0);
      setPhase("lyrics");
    } catch (e) {
      alert(e.message || "Couldn't write lyrics — try again");
    } finally { setLyricsBusy(false); }
  };

  const buy = async () => {
    if (genre.trim().length < 2 || mood.trim().length < 3 || lyrics.trim().length < 10) return;
    setPhase("starting");
    try {
      const r = await fetch(`${API}/api/guest-music/checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ genre, mood, lyrics, email: email || null, origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok || !d.checkout_url) throw new Error(d.detail || "Checkout failed");
      localStorage.setItem(lsKey(d.order_id), d.token);
      window.location.href = d.checkout_url;
    } catch (e) {
      setPhase("lyrics");
      alert(e.message || "Could not start checkout");
    }
  };

  const playTrack = (t) => {
    if (playing?.id === t.id) { audioRef.current?.pause(); setPlaying(null); return; }
    setProg(0);
    setPlaying(t);
    setTimeout(() => { if (audioRef.current) { audioRef.current.src = `${API}${t.stream_url}`; audioRef.current.play().catch(() => {}); } }, 50);
  };
  const nextTrack = () => {
    if (!tracks.length) return;
    if (shuffle && tracks.length > 1) {
      let n;
      do { n = Math.floor(Math.random() * tracks.length); } while (tracks[n].id === playing?.id);
      return playTrack(tracks[n]);
    }
    const i = tracks.findIndex((x) => x.id === playing?.id);
    playTrack(tracks[(i + 1) % tracks.length]);
  };

  return (
    <div className="h-full w-full overflow-y-auto no-scrollbar bg-[#0A0A0A] text-white" data-testid="music-buyasong">
      <audio ref={audioRef} onEnded={nextTrack} onTimeUpdate={(e) => { const a = e.target; if (a.duration) setProg(a.currentTime / a.duration); }} className="hidden" />

      {/* HERO */}
      <div className="px-5 pt-7 pb-5 border-b-2 border-[#1a1a1a] bg-gradient-to-b from-[#141007] to-[#0A0A0A]">
        <div className="flex justify-center mb-4">
          <div className="border-2 border-black bg-[#FFD028] p-1.5 shadow-[5px_5px_0px_#000] rounded-2xl" data-testid="buyasong-brand-logo">
            <MisteckMark size={88} />
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 border-2 border-black bg-[#FFE08A] text-black font-mono text-[9px] font-black uppercase tracking-widest px-2 py-1 shadow-[2px_2px_0px_#000] mb-4">
          <Music2 size={11} /> Misteck Music
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-[0.95] text-white mb-2">Buy your song.<br /><span className="text-[#FFE08A]">Made in seconds.</span></h1>
        <p className="font-mono text-sm text-white/60 leading-relaxed mb-4">Type a genre and a mood, preview your lyrics free, then pay $0.99 — get the full song plus 60s &amp; 30s cuts, royalty-free.</p>
        <button onClick={() => document.getElementById("buy-song-section")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-buy-btn"
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-serif text-lg py-3 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <Sparkles size={16} /> Buy Your Song · $0.99
        </button>
      </div>

      {/* SECTION 1 — BUY A SONG */}
      <div id="buy-song-section" className="px-5 py-6 space-y-4" data-testid="buy-song-section">
        {phase === "ready" ? (
          <div className="border-2 border-[#22C55E] bg-[#0e150e] p-5 shadow-[4px_4px_0px_#000] text-center" data-testid="song-ready">
            <Sparkles size={22} className="text-[#22C55E] mx-auto mb-2" />
            <p className="font-serif text-2xl text-white mb-1">Your song is ready</p>
            <p className="font-mono text-[11px] text-white/50 mb-4">Royalty-free · no watermark · yours forever</p>
            <a href={downloadUrl} data-testid="download-song-btn"
              className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-mono text-[11px] font-black uppercase tracking-widest py-3 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none mb-2">
              <Download size={15} /> Download full song
            </a>
            <div className="flex gap-2 mb-3">
              <a href={downloads?.cut60 ? `${API}${downloads.cut60}` : downloadUrl} data-testid="download-60-btn"
                className="flex-1 flex items-center justify-center gap-1.5 border-2 border-black bg-[#0e0e0e] text-white font-mono text-[9px] font-black uppercase tracking-widest py-2.5 active:translate-y-[1px]">
                <Download size={12} /> 60s cut
              </a>
              <a href={downloads?.reel30 ? `${API}${downloads.reel30}` : downloadUrl} data-testid="download-30-btn"
                className="flex-1 flex items-center justify-center gap-1.5 border-2 border-black bg-[#0e0e0e] text-white font-mono text-[9px] font-black uppercase tracking-widest py-2.5 active:translate-y-[1px]">
                <Download size={12} /> 30s reel
              </a>
            </div>
            <button onClick={() => nav("/join")} data-testid="save-account-btn"
              className="w-full flex items-center justify-center gap-1.5 font-mono text-[10px] text-[#FFE08A] underline underline-offset-4 py-1">
              Create a free account to save it to your Vault <ArrowRight size={12} />
            </button>
          </div>
        ) : phase === "processing" || phase === "starting" ? (
          <div className="border-2 border-black bg-[#141414] p-6 shadow-[4px_4px_0px_#000] text-center" data-testid="song-processing">
            <Loader2 size={24} className="text-[#FFE08A] mx-auto animate-spin mb-3" />
            <p className="font-serif text-xl text-white mb-1">{phase === "starting" ? "Opening secure checkout…" : "Composing your song…"}</p>
            <p className="font-mono text-[11px] text-white/50 leading-relaxed">Your full song takes 2–4 minutes. Keep this tab open — or we'll email the link the moment it's done.</p>
          </div>
        ) : phase === "lyrics" ? (
          <div className="border-2 border-black bg-[#141414] p-4 shadow-[4px_4px_0px_#000] space-y-3" data-testid="lyrics-step">
            <div className="flex items-center justify-between">
              <label className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A]">Your lyrics — edit anything</label>
              <span className="font-mono text-[8px] text-white/40 uppercase truncate max-w-[45%]">{genre} · {mood}</span>
            </div>
            <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={12} data-testid="lyrics-editor"
              className="w-full bg-[#0e0e0e] text-cream font-mono text-[11px] leading-relaxed p-3 border-2 border-black focus:border-[#FFE08A] outline-none resize-y placeholder:text-white/25" />
            <div className="flex gap-2">
              <button onClick={() => setPhase("idle")} data-testid="lyrics-back-btn"
                className="flex items-center justify-center gap-1 border-2 border-white/30 bg-black text-white/70 font-mono text-[9px] font-black uppercase tracking-widest px-3 py-2.5">Back</button>
              <button onClick={previewLyrics} disabled={lyricsRem <= 0 || lyricsBusy} data-testid="lyrics-newversion-btn"
                className="flex-1 flex items-center justify-center gap-1.5 border-2 border-black bg-[#0e0e0e] text-white font-mono text-[9px] font-black uppercase tracking-widest px-3 py-2.5 disabled:opacity-40">
                {lyricsBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} New version{lyricsRem > 0 ? ` (${lyricsRem} left)` : " (none left)"}
              </button>
            </div>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="song-email-input"
              placeholder="Email (so we can send your song if you close the tab)"
              className="w-full bg-[#0e0e0e] text-cream font-mono text-[11px] px-3 py-2.5 border-2 border-black focus:border-[#FFE08A] outline-none placeholder:text-white/25" />
            <button onClick={buy} disabled={lyrics.trim().length < 10} data-testid="buy-song-btn"
              className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-serif text-lg py-3 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40">
              <Sparkles size={16} /> Buy Your Song · $0.99
            </button>
            <p className="flex items-center justify-center gap-1.5 font-mono text-[9px] text-white/40 text-center leading-relaxed">
              <ShieldCheck size={11} className="text-[#FFE08A] shrink-0" /> You only pay after you're happy with the lyrics. Full song + 60s + 30s cuts, royalty-free, no watermark.
            </p>
          </div>
        ) : (
          <div className="border-2 border-black bg-[#141414] p-4 shadow-[4px_4px_0px_#000] space-y-3">
            {phase === "failed" && <p className="font-mono text-[10px] text-red-400" data-testid="song-failed">That render didn't complete — please try again (you won't be double-charged for a new song).</p>}
            <div className="space-y-1">
              <label className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A]">Genre</label>
              <input value={genre} onChange={(e) => setGenre(e.target.value)} data-testid="song-genre-input"
                placeholder="Type a genre... (e.g., Viking Doom, Deep House, Drift Phonk)"
                className="w-full bg-[#0e0e0e] text-cream font-mono text-[12px] px-3 py-2.5 border-2 border-black focus:border-[#FFE08A] outline-none placeholder:text-white/25" />
            </div>
            <div className="space-y-1">
              <label className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A]">Mood / Activity</label>
              <input value={mood} onChange={(e) => setMood(e.target.value)} data-testid="song-mood-input"
                placeholder="Type a mood or activity... (e.g., By myself gym pump, romantic drive-in theatre)"
                className="w-full bg-[#0e0e0e] text-cream font-mono text-[12px] px-3 py-2.5 border-2 border-black focus:border-[#FFE08A] outline-none placeholder:text-white/25" />
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="activity-chips">
              {ACTIVITIES.map((a) => (
                <button key={a} onClick={() => setMood(a)} data-testid={`activity-${a.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`font-mono text-[9px] font-black uppercase tracking-wide px-2.5 py-1.5 border-2 border-black ${mood === a ? "bg-[#FFE08A] text-black shadow-[2px_2px_0px_#000]" : "bg-[#0e0e0e] text-white/60"}`}>{a}</button>
              ))}
            </div>
            <button onClick={previewLyrics} disabled={genre.trim().length < 2 || mood.trim().length < 3 || lyricsBusy} data-testid="preview-lyrics-btn"
              className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-serif text-lg py-3 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40">
              {lyricsBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Preview my lyrics — free
            </button>
            <p className="flex items-center justify-center gap-1.5 font-mono text-[9px] text-white/40 text-center leading-relaxed">
              <ShieldCheck size={11} className="text-[#FFE08A] shrink-0" /> See &amp; edit your lyrics first. You only pay $0.99 when ready — then get the full song + 60s + 30s cuts.
            </p>
          </div>
        )}
      </div>

      {/* SECTION 2 — MISTECK RADIO */}
      <div className="px-5 py-6 border-t-2 border-[#1a1a1a]" data-testid="radio-section">
        <div className="flex items-center gap-2 mb-3">
          <Radio size={16} className="text-[#C9B6FF]" />
          <h2 className="font-serif text-lg text-white flex-1">Misteck Radio</h2>
          {tracks.length > 1 && (
            <button onClick={() => setShuffle((s) => !s)} data-testid="radio-shuffle-btn" title={shuffle ? "Shuffle on" : "Shuffle off"}
              className={`h-7 px-2 flex items-center gap-1 border-2 border-black font-mono text-[8px] font-black uppercase tracking-widest ${shuffle ? "bg-[#C9B6FF] text-black" : "bg-[#141414] text-white/60"}`}>
              <Shuffle size={11} /> {shuffle ? "On" : "Off"}
            </button>
          )}
          {playing && <button onClick={nextTrack} data-testid="radio-next-btn" className="w-7 h-7 grid place-items-center border-2 border-black bg-[#141414]"><SkipForward size={12} className="text-white" /></button>}
        </div>
        <p className="font-mono text-[10px] text-white/40 mb-3">Community tracks over 2 minutes — stream, don't download.</p>
        {tracks.length === 0 ? (
          <div className="border-2 border-dashed border-[#2a2a2a] p-6 text-center" data-testid="radio-empty">
            <p className="font-mono text-[10px] text-white/40">No community tracks yet — be the first to share one.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {tracks.map((t) => (
              <button key={t.id} onClick={() => playTrack(t)} data-testid={`radio-track-${t.id}`}
                className={`relative overflow-hidden w-full flex items-center gap-3 border-2 border-black p-2.5 text-left ${playing?.id === t.id ? "bg-[#1a1630]" : "bg-[#141414]"}`}>
                <span className="w-8 h-8 grid place-items-center border-2 border-black bg-[#C9B6FF] text-black shrink-0">
                  {playing?.id === t.id ? <Pause size={13} /> : <Play size={13} />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="font-mono text-[11px] font-black text-white truncate block">{t.title}</span>
                  <span className="font-mono text-[8px] text-white/40 uppercase truncate block">{t.genre || "MUSIC"} · Created with Misteck</span>
                </span>
                {playing?.id === t.id && (
                  <span data-testid={`radio-progress-${t.id}`} className="absolute left-0 bottom-0 h-[3px] bg-[#00E5C0] transition-[width] duration-300 ease-linear" style={{ width: `${Math.round(prog * 100)}%` }} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* SECTION 3 — JOIN */}
      <div className="px-5 py-8 border-t-2 border-[#1a1a1a] bg-gradient-to-b from-[#0A0A0A] to-[#141007] text-center">
        <h2 className="font-serif text-2xl text-white mb-1">Want the whole studio?</h2>
        <p className="font-mono text-[11px] text-white/50 mb-4 leading-relaxed">Unlimited songs, videos, reels & your own Vault — the entire Misteck ecosystem.</p>
        <button onClick={() => nav("/join")} data-testid="join-more-btn"
          className="inline-flex items-center gap-2 border-2 border-black bg-[#FFE08A] text-black font-serif text-lg px-8 py-3 shadow-[5px_5px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none">
          Join for more <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
